import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Checkbox,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Snackbar,
  FormControlLabel,
  Divider,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as AgentIcon,
  Storage as McpIcon,
  Api as ApiIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api } from '../services/api-client';

// =============================================================================
// Types
// =============================================================================

interface ServiceInfo {
  id: string;
  name?: string;
  description?: string;
  type: string;
  active?: boolean;
}

interface AgentData {
  id: string;
  name?: string;
  description?: string;
  allowed_services?: string[];
  services?: string[];
  permissions?: {
    mcp_servers: string[];
    agents: string[];
    apis: string[];
  };
}

interface PermissionsResponse {
  agents: AgentData[];
  available_services: {
    mcp_server: ServiceInfo[];
    agent: ServiceInfo[];
    api: ServiceInfo[];
    other: ServiceInfo[];
  };
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// =============================================================================
// Components
// =============================================================================

function ServiceCheckbox({
  service,
  checked,
  onChange,
  disabled,
}: {
  service: ServiceInfo;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
}) {
  const isInactive = service.active === false;

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled || isInactive}
          size="small"
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                color: isInactive ? 'text.disabled' : 'text.primary',
              }}
            >
              {service.id}
            </Typography>
            {service.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  maxWidth: 300,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {service.description}
              </Typography>
            )}
          </Box>
          {isInactive && (
            <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
          )}
        </Box>
      }
      sx={{
        width: '100%',
        m: 0,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    />
  );
}

function AgentCard({
  agent,
  availableServices,
  onSave,
  saving,
  savingAgentId,
}: {
  agent: AgentData;
  availableServices: PermissionsResponse['available_services'];
  onSave: (agentId: string, allowedServices: string[]) => void;
  saving: boolean;
  savingAgentId: string | null;
}) {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'developer';

  // Get current allowed services
  const originalAllowed = agent.allowed_services || agent.services || [];
  const [allowedServices, setAllowedServices] = useState<string[]>(originalAllowed);
  const [description, setDescription] = useState(agent.description || '');
  const [expanded, setExpanded] = useState(false);

  // Reset when agent changes
  useEffect(() => {
    setAllowedServices(agent.allowed_services || agent.services || []);
    setDescription(agent.description || '');
  }, [agent]);

  const hasChanges =
    JSON.stringify([...allowedServices].sort()) !== JSON.stringify([...originalAllowed].sort()) ||
    description !== (agent.description || '');

  const handleToggle = (serviceId: string, checked: boolean) => {
    if (checked) {
      setAllowedServices([...allowedServices, serviceId]);
    } else {
      setAllowedServices(allowedServices.filter(s => s !== serviceId));
    }
  };

  const handleSave = () => {
    onSave(agent.id, allowedServices);
  };

  const handleCancel = () => {
    setAllowedServices(originalAllowed);
    setDescription(agent.description || '');
  };

  const mcpCount = availableServices.mcp_server.filter(s => allowedServices.includes(s.id)).length;
  const agentCount = availableServices.agent.filter(s => allowedServices.includes(s.id) && s.id !== agent.id).length;
  const apiCount = availableServices.api.filter(s => allowedServices.includes(s.id)).length;

  const isSaving = saving && savingAgentId === agent.id;

  return (
    <Card sx={{ mb: 2 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
            <AgentIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>
                {agent.id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {mcpCount} MCP
                </Typography>
                <Typography variant="caption" color="text.secondary">•</Typography>
                <Typography variant="caption" color="text.secondary">
                  {agentCount} Agent{agentCount !== 1 ? 's' : ''}
                </Typography>
                {apiCount > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary">•</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {apiCount} API{apiCount !== 1 ? 's' : ''}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            {hasChanges && (
              <Chip label="Unsaved Changes" size="small" color="warning" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={!canEdit}
            size="small"
            placeholder="Describe what this agent does..."
            sx={{ mb: 3 }}
          />

          {/* Agent Access (A2A) */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <AgentIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight="medium">
                Agent Access (A2A)
              </Typography>
              <Chip
                label={`${agentCount} selected`}
                size="small"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
            <Box
              sx={{
                pl: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
              }}
            >
              {availableServices.agent.filter(s => s.id !== agent.id).length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No other agents available
                </Typography>
              ) : (
                availableServices.agent
                  .filter(s => s.id !== agent.id)
                  .map(service => (
                    <ServiceCheckbox
                      key={service.id}
                      service={service}
                      checked={allowedServices.includes(service.id)}
                      onChange={(checked) => handleToggle(service.id, checked)}
                      disabled={!canEdit}
                    />
                  ))
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* MCP Server Access */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <McpIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight="medium">
                MCP Server Access
              </Typography>
              <Chip
                label={`${mcpCount} selected`}
                size="small"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
            <Box
              sx={{
                pl: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {availableServices.mcp_server.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No MCP servers available
                </Typography>
              ) : (
                availableServices.mcp_server.map(service => (
                  <ServiceCheckbox
                    key={service.id}
                    service={service}
                    checked={allowedServices.includes(service.id)}
                    onChange={(checked) => handleToggle(service.id, checked)}
                    disabled={!canEdit}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* API Access */}
          {availableServices.api.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <ApiIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight="medium">
                    API Access
                  </Typography>
                  <Chip
                    label={`${apiCount} selected`}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Box>
                <Box
                  sx={{
                    pl: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {availableServices.api.map(service => (
                    <ServiceCheckbox
                      key={service.id}
                      service={service}
                      checked={allowedServices.includes(service.id)}
                      onChange={(checked) => handleToggle(service.id, checked)}
                      disabled={!canEdit}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Actions */}
          {canEdit && hasChanges && (
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end'
              }}
            >
              <Button
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        px: 4,
        bgcolor: 'action.hover',
        borderRadius: 2,
        border: '2px dashed',
        borderColor: 'divider',
      }}
    >
      <AgentIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        No Agents Found
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
        Agents will appear here once they are registered in the command_center_agents collection.
        Make sure your agents are properly configured in Firestore.
      </Typography>
    </Box>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function AgentPermissions() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'developer';

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [savingAgentId, setSavingAgentId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch permissions data
  const { data, isLoading, error } = useQuery<PermissionsResponse>(
    'agentPermissions',
    () => api.getAgentPermissions(),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ agentId, services }: { agentId: string; services: string[] }) =>
      api.updateAgentPermissions(agentId, services),
    {
      onMutate: ({ agentId }) => {
        setSavingAgentId(agentId);
      },
      onSuccess: (_, { agentId }) => {
        queryClient.invalidateQueries('agentPermissions');
        setSnackbar({
          open: true,
          message: `Permissions updated for ${agentId}`,
          severity: 'success'
        });
      },
      onError: (error: Error, { agentId }) => {
        setSnackbar({
          open: true,
          message: `Failed to update ${agentId}: ${error.message}`,
          severity: 'error'
        });
      },
      onSettled: () => {
        setSavingAgentId(null);
      },
    }
  );

  const handleSave = (agentId: string, allowedServices: string[]) => {
    updateMutation.mutate({ agentId, services: allowedServices });
  };

  // Filter agents
  const agents = data?.agents || [];
  const availableServices = data?.available_services || {
    mcp_server: [],
    agent: [],
    api: [],
    other: [],
  };

  const filteredAgents = useMemo(() => {
    if (!searchQuery) return agents;
    const query = searchQuery.toLowerCase();
    return agents.filter(agent =>
      agent.id.toLowerCase().includes(query) ||
      agent.description?.toLowerCase().includes(query)
    );
  }, [agents, searchQuery]);

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Agent Permissions</Typography>
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading agent permissions...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Agent Permissions</Typography>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => queryClient.invalidateQueries('agentPermissions')}>
              Retry
            </Button>
          }
        >
          Failed to load permissions. Please try again.
        </Alert>
      </Box>
    );
  }

  // Stats
  const totalMcpServers = availableServices.mcp_server.length;
  const totalAgentServices = availableServices.agent.length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Agent Permissions</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure which services each agent can access
        </Typography>
      </Box>

      {/* Info banner */}
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
          You have read-only access. Contact an admin to modify permissions.
        </Alert>
      )}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          icon={<AgentIcon />}
          label={`${agents.length} Agent${agents.length !== 1 ? 's' : ''}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          icon={<McpIcon />}
          label={`${totalMcpServers} MCP Server${totalMcpServers !== 1 ? 's' : ''}`}
          variant="outlined"
        />
        <Chip
          icon={<ApiIcon />}
          label={`${totalAgentServices} A2A Target${totalAgentServices !== 1 ? 's' : ''}`}
          variant="outlined"
        />
      </Box>

      {/* Search */}
      <TextField
        placeholder="Search agents..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        sx={{ mb: 3, minWidth: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {/* Agents list */}
      {filteredAgents.length === 0 ? (
        searchQuery ? (
          <Alert severity="info">
            No agents match "{searchQuery}"
          </Alert>
        ) : (
          <EmptyState />
        )
      ) : (
        filteredAgents.map(agent => (
          <AgentCard
            key={agent.id}
            agent={agent}
            availableServices={availableServices}
            onSave={handleSave}
            saving={updateMutation.isLoading}
            savingAgentId={savingAgentId}
          />
        ))
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
